import { useEffect, useState, type FC } from "react";
import type { InvoiceFieldValidationModel } from "../../../../models/InvoiceModels";
import { useTranslation } from "react-i18next";
import Subheading from "../../../atoms/typography/Subheading";
import TextField from "../../../atoms/inputs/TextField";
import { Col, Row } from "react-grid-system";
import IconButton from "../../../atoms/buttons/IconButton";
import { SaveIconMedium } from "../../../atoms/icons/SaveIcon";
import BodySmall from "../../../atoms/typography/BodySmall";
import { theme } from "../../../../theme";
import { useUpdateInvoiceDetails } from "../../../../graphql/hooks/invoices/useUpdateInvoiceDetails";
import Alert from "../../../molecules/Alert";
import dayjs from "dayjs";
import Tooltip from "../../../molecules/Tooltip";
import ChevronButton from "../../../atoms/buttons/ChevronButtons";
import { useRegisterSaveSection } from "../SaveSectionsContext";
import { SlideOutHeight } from "../../../atoms/SlideOutHeight";
import DatePicker from "../../../molecules/filters/DatePicker";
import Divider from "../../../atoms/Divider";
import { HorizontalSpacer } from "../../../atoms/Spacers";
import Checkbox from "../../../atoms/inputs/Checkbox";

interface Props {
  invoiceFields: Array<InvoiceFieldValidationModel>;
  invoiceId: string;
  dueDate?: Date;
  invoiceDate?: Date;
  invoiceNumber: string;
  totalAmountWithVat?: number;
  totalVatAmount?: number;
  rounding?: number;
  shippingCost?: number;
  currencyCode?: string;
  invoiceFee?: number;
  shippingFeeVatPercentage?: number;
  isEditable: boolean;
  isCreditInvoice?: boolean;
  vatReverseCharge?: boolean;
}

interface InvoiceDetailsData {
  invoiceId: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  totalIncludingVAT?: string;
  totalVAT?: string;
  shippingCost?: string;
  rounding?: string;
  currencyCode?: string;
  invoiceFee?: string;
  shippingFeeVatPercentage?: string;
  isCreditInvoice?: boolean;
  vatReverseCharge?: boolean;
}

const InvoiceDetailsDetailsSection: FC<Props> = ({
  invoiceFields,
  invoiceId,
  invoiceNumber,
  dueDate,
  invoiceDate,
  totalAmountWithVat,
  totalVatAmount,
  rounding,
  shippingCost,
  currencyCode,
  shippingFeeVatPercentage,
  invoiceFee,
  isEditable,
  isCreditInvoice,
  vatReverseCharge,
}) => {
  const { t } = useTranslation();
  const [dataHasBeenUpdated, setDataHasBeenUpdated] = useState(false);

  const [invoiceDetailsData, setInvoiceDetailsData] =
    useState<InvoiceDetailsData | null>(null);
  const [saveSuccessful, setSaveSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setInvoiceDetailsData({
      invoiceId,
      invoiceNumber,
      invoiceDate: invoiceDate
        ? dayjs(invoiceDate).format("YYYY-MM-DD")
        : undefined,
      dueDate: dueDate ? dayjs(dueDate).format("YYYY-MM-DD") : undefined,
      totalIncludingVAT: totalAmountWithVat?.toString(),
      totalVAT: totalVatAmount?.toString(),
      shippingCost: shippingCost?.toString(),
      rounding: rounding?.toString(),
      currencyCode: currencyCode,
      invoiceFee: invoiceFee?.toString(),
      shippingFeeVatPercentage: shippingFeeVatPercentage?.toString(),
      isCreditInvoice: isCreditInvoice,
      vatReverseCharge: vatReverseCharge,
    });
  }, [
    invoiceId,
    invoiceNumber,
    invoiceDate,
    dueDate,
    totalAmountWithVat,
    totalVatAmount,
    shippingCost,
    rounding,
    currencyCode,
    invoiceFee,
    shippingFeeVatPercentage,
    isCreditInvoice,
    vatReverseCharge,
  ]);

  const fieldTypeToDetailsKey: Record<string, keyof InvoiceDetailsData> = {
    InvoiceNumber: "invoiceNumber",
    InvoiceDate: "invoiceDate",
    DueDate: "dueDate",
    TotalAmountIncludingVat: "totalIncludingVAT",
    TotalVatAmount: "totalVAT",
    ShippingFeeAmount: "shippingCost",
    Rounding: "rounding",
    CurrencyCode: "currencyCode",
    InvoiceFeeAmount: "invoiceFee",
    ShippingFeeVatPercentage: "shippingFeeVatPercentage",
    IsCreditInvoice: "isCreditInvoice",
    VatReverseCharge: "vatReverseCharge",
  };

  const invoiceDetailValueByFieldType: Record<string, string | undefined> = {
    InvoiceNumber: invoiceNumber,
    InvoiceDate: invoiceDate
      ? dayjs(invoiceDate).format("YYYY-MM-DD")
      : undefined,
    DueDate: dueDate ? dayjs(dueDate).format("YYYY-MM-DD") : undefined,
    TotalAmountIncludingVat: totalAmountWithVat?.toString(),
    TotalVatAmount: totalVatAmount?.toString(),
    ShippingFeeAmount: shippingCost?.toString(),
    ShippingFeeVatPercentage: shippingFeeVatPercentage?.toString(),
    Rounding: rounding?.toString(),
    CurrencyCode: currencyCode,
    InvoiceFeeAmount: invoiceFee?.toString(),
    IsCreditInvoice: isCreditInvoice?.toString(),
  };

  // Returns an error message if the field has a validation error AND the value
  // hasn't changed since the (invalid) validation was recorded.
  const getFieldError = (fieldType: string) => {
    const validationField = invoiceFields.find(
      (f) => f.fieldType === fieldType,
    );
    if (!validationField || validationField.isValid) return undefined;

    const validationValue = validationField.value ?? "";
    const invoiceValue = invoiceDetailValueByFieldType[fieldType] ?? "";

    const normalizedValidation = parseFloat(validationValue);
    const normalizedInvoice = parseFloat(invoiceValue);
    const valuesMatch =
      !isNaN(normalizedValidation) && !isNaN(normalizedInvoice)
        ? normalizedValidation === normalizedInvoice
        : validationValue === invoiceValue;

    if (valuesMatch) {
      return t("invoices.fieldIsInvalid", {
        fieldName: t(`invoices.${fieldType}`),
      });
    }
    return undefined;
  };

  const getVatTotalsMatchError = () => {
    const vatTotalsMatch = invoiceFields.find(
      (f) => f.fieldType === "VatTotalsMatch",
    );
    if (!vatTotalsMatch || vatTotalsMatch.isValid) return undefined;

    return t("invoices.vatTotalsMismatch");
  };

  const onUpdateField =(fieldType: string, value: string | boolean) => {
    if (!dataHasBeenUpdated) setDataHasBeenUpdated(true);
    const key = fieldTypeToDetailsKey[fieldType];
    if (!key) return;
    setInvoiceDetailsData((prev) => {
      if (!prev) return prev;
      return { ...prev, [key]: value };
    });
  };

  const onCompletedSave = (res?: any) => {
    if (res?.invoice?.updateInvoiceDetails?.success) {
      setErrorMessage("");
      setSaveSuccessful(true);
      setTimeout(() => setSaveSuccessful(false), 5000);
      setDataHasBeenUpdated(false);
    } else {
      setErrorMessage(t("invoices.saveFailed"));
    }
  };

  const onErrorSave = (e?: any) => {
    console.error(e);
    setErrorMessage(e?.toString());
  };

  const { updateInvoiceDetails, error, loading } = useUpdateInvoiceDetails({
    onCompleted: onCompletedSave,
    onError: onErrorSave,
  });

  const onClickSaveSection = async () => {
    if (!invoiceDetailsData) return;

    try {
      await updateInvoiceDetails({
        variables: {
          input: {
            invoiceId: invoiceDetailsData.invoiceId,
            invoiceNumber: invoiceDetailsData.invoiceNumber,
            invoiceDate: invoiceDetailsData.invoiceDate,
            dueDate: invoiceDetailsData.dueDate,
            totalIncludingVAT:
              invoiceDetailsData.totalIncludingVAT !== undefined
                ? parseFloat(invoiceDetailsData.totalIncludingVAT)
                : undefined,
            totalVAT:
              invoiceDetailsData.totalVAT !== undefined
                ? parseFloat(invoiceDetailsData.totalVAT)
                : undefined,
            shippingCost:
              invoiceDetailsData.shippingCost !== undefined
                ? parseFloat(invoiceDetailsData.shippingCost)
                : undefined,
            rounding:
              invoiceDetailsData.rounding !== undefined
                ? parseFloat(invoiceDetailsData.rounding)
                : undefined,
            currencyCode: invoiceDetailsData.currencyCode,
            invoiceFee:
              invoiceDetailsData.invoiceFee !== undefined
                ? parseFloat(invoiceDetailsData.invoiceFee)
                : undefined,
            shippingFeeVatPercentage:
              invoiceDetailsData.shippingFeeVatPercentage !== undefined
                ? parseFloat(invoiceDetailsData.shippingFeeVatPercentage)
                : undefined,
            isCreditInvoice: invoiceDetailsData.isCreditInvoice,
            // Only send when toggled: the backend rewrites the flag on every row.
            vatReverseCharge:
              invoiceDetailsData.vatReverseCharge !== (vatReverseCharge ?? false)
                ? invoiceDetailsData.vatReverseCharge
                : undefined,
          },
        },
      });
    } catch (e) {
      console.error(e);
      setErrorMessage(e?.toString() || t("invoices.saveFailed"));
    }
  };

  useRegisterSaveSection({
    id: "invoiceDetails",
    label: t("invoiceValidation.saveInvoiceDetails"),
    isDirty: dataHasBeenUpdated,
    onSave: onClickSaveSection,
    loading,
  });

  return (
    <div
      style={{
        backgroundColor: dataHasBeenUpdated
          ? theme.colors.info05
          : "transparent",
        padding: 8,
        borderRadius: 4,
      }}
    >
      <Row nogutter justify="between">
        <Subheading>{t("invoices.invoiceInformation")}</Subheading>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <ChevronButton
            type={open ? "up" : "down"}
            onClick={() => setOpen(!open)}
          />
          {saveSuccessful ? (
            <BodySmall fontweight={500} color={theme.colors.success}>
              {t("random.saved")}
            </BodySmall>
          ) : (
            <Tooltip text={t("invoiceValidation.saveSection")}>
              <IconButton
                icon={
                  <SaveIconMedium
                    color={
                      dataHasBeenUpdated
                        ? theme.colors.white
                        : theme.colors.text
                    }
                  />
                }
                onClick={onClickSaveSection}
                size="medium"
                loading={loading}
                color={
                  dataHasBeenUpdated ? theme.colors.info : theme.colors.info05
                }
                disabled={!isEditable}
              />
            </Tooltip>
          )}
        </div>
      </Row>
      <SlideOutHeight isVisible={open}>
        <Row>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.InvoiceNumber")}
              value={invoiceDetailsData?.invoiceNumber ?? ""}
              name={"invoiceNumber"}
              fullWidth
              onChange={(value) => onUpdateField("InvoiceNumber", value)}
              errorMessage={getFieldError("InvoiceNumber")}
              disabled={!isEditable}
            />
          </Col>
          <Col
            xs={12}
            md={6}
            style={{
              padding: "8px 16px",
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Checkbox
              title={t("invoices.creditInvoice")}
              checked={invoiceDetailsData?.isCreditInvoice ?? false}
              onChange={(value: boolean) =>
                onUpdateField("IsCreditInvoice", value)
              }
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <DatePicker
              title={t("invoices.InvoiceDate")}
              value={
                invoiceDetailsData?.invoiceDate
                  ? new Date(invoiceDetailsData?.invoiceDate)
                  : undefined
              }
              onChange={(value) =>
                onUpdateField("InvoiceDate", dayjs(value).format("YYYY-MM-DD"))
              }
              errorMessage={getFieldError("InvoiceDate")}
              fullWidth
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <DatePicker
              title={t("invoices.DueDate")}
              value={
                invoiceDetailsData?.dueDate
                  ? new Date(invoiceDetailsData?.dueDate)
                  : undefined
              }
              onChange={(value) =>
                onUpdateField("DueDate", dayjs(value).format("YYYY-MM-DD"))
              }
              errorMessage={getFieldError("DueDate")}
              fullWidth
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.TotalAmountIncludingVat")}
              value={invoiceDetailsData?.totalIncludingVAT ?? ""}
              name={"totalIncludingVAT"}
              fullWidth
              onChange={(value) =>
                onUpdateField("TotalAmountIncludingVat", value)
              }
              errorMessage={getFieldError("TotalAmountIncludingVat")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.TotalVatAmount")}
              value={invoiceDetailsData?.totalVAT ?? ""}
              name={"totalVAT"}
              fullWidth
              onChange={(value) => onUpdateField("TotalVatAmount", value)}
              errorMessage={
                getFieldError("TotalVatAmount") ?? getVatTotalsMatchError()
              }
              disabled={!isEditable}
            />
          </Col>

          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.Rounding")}
              value={invoiceDetailsData?.rounding ?? ""}
              name={"rounding"}
              fullWidth
              onChange={(value) => onUpdateField("Rounding", value)}
              errorMessage={getFieldError("Rounding")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.currencyCode")}
              value={invoiceDetailsData?.currencyCode ?? ""}
              name={"currencyCode"}
              fullWidth
              onChange={(value) => onUpdateField("CurrencyCode", value)}
              errorMessage={getFieldError("CurrencyCode")}
              disabled={!isEditable}
            />
          </Col>
        </Row>
        <HorizontalSpacer spacing={1} />
        <Divider />
        <Row>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.ShippingFeeAmount")}
              value={invoiceDetailsData?.shippingCost ?? ""}
              name={"shippingCost"}
              fullWidth
              onChange={(value) => onUpdateField("ShippingFeeAmount", value)}
              errorMessage={getFieldError("ShippingFeeAmount")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.ShippingFeeVatPercentage")}
              value={invoiceDetailsData?.shippingFeeVatPercentage ?? ""}
              name={"shippingFeeVatPercentage"}
              fullWidth
              onChange={(value) =>
                onUpdateField("ShippingFeeVatPercentage", value)
              }
              errorMessage={getFieldError("ShippingFeeVatPercentage")}
              disabled={!isEditable}
            />
          </Col>
          <Col xs={12} md={6} style={{ padding: "8px 16px" }}>
            <TextField
              label={t("invoices.InvoiceFeeAmount")}
              value={invoiceDetailsData?.invoiceFee ?? ""}
              name={"invoiceFee"}
              fullWidth
              onChange={(value) => onUpdateField("InvoiceFeeAmount", value)}
              errorMessage={getFieldError("InvoiceFeeAmount")}
              disabled={!isEditable}
            />
          </Col>
          <Col
            xs={12}
            md={6}
            style={{
              padding: "8px 16px",
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Checkbox
              title={t("invoices.VatReverseCharge")}
              checked={invoiceDetailsData?.vatReverseCharge ?? false}
              onChange={(value: boolean) =>
                onUpdateField("VatReverseCharge", value)
              }
              disabled={!isEditable}
            />
          </Col>
        </Row>
      </SlideOutHeight>

      {(errorMessage || error) && (
        <Alert
          type="error"
          message={errorMessage}
          title="Ändringar av fakturauppgifter kunde inte sparas"
        />
      )}
    </div>
  );
};

export default InvoiceDetailsDetailsSection;
